import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9opqfqftmtnfcfh0bqg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormLoading(false);
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(String(serverMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请重试');
      setFormStatus('error');
    } finally {
      setFormLoading(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51黑料网',
    url: 'https://51heiliaowang.com/',
    description: '51黑料网分区导航页，集中展示一区二区、每日大赛专区等分类入口，提供最新黑料分区内容。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@51heiliaowang.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51黑料网是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51黑料网是一个分区导航平台，集中展示黑料一区、黑料二区及每日大赛专区的最新内容，提供一站式黑料资讯浏览服务。',
        },
      },
      {
        '@type': 'Question',
        name: '如何查看不同分区的内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过顶部导航栏或首页的分区卡片，直接进入黑料一区、黑料二区或每日大赛专区，按区浏览您感兴趣的内容。',
        },
      },
      {
        '@type': 'Question',
        name: '网站内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51黑料网保持24小时实时更新，确保您能第一时间获取最新的一区二区及每日大赛相关信息。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系我们？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单或发送邮件至 contact@51heiliaowang.com 与我们取得联系。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: '关于我们' },
                ]}
              />
            </div>
          </div>

          {/* Page Header */}
          <section className="w-full px-4 md:px-6 pb-8">
            <div className="max-w-5xl mx-auto text-center">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-3">
                关于51黑料网
              </h1>
              <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto">
                一区二区 · 每日大赛 · 黑料分区导航平台
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section id="brand" className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
                <div>
                  <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">
                    品牌介绍
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mt-2 mb-4">
                    领先的黑料资讯分区导航平台
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
                    51黑料网成立于2024年，是国内领先的<strong>黑料资讯分区导航平台</strong>。
                    我们致力于为用户提供最全面、最及时的黑料内容聚合服务，
                    通过<strong>一区二区</strong>和<strong>每日大赛专区</strong>三大核心分区，
                    让用户能够快速定位并浏览自己感兴趣的内容板块。
                  </p>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                    我们拥有一支专业的编辑团队，全天候追踪各大平台的最新动态，
                    确保用户能够第一时间获取到最新鲜、最劲爆的<strong>黑料分区内容</strong>。
                    截至目前，平台日均访问量已突破50万，成为行业内最具影响力的导航入口之一。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    {[
                      { icon: 'ri-shield-check-line', label: '内容真实可靠' },
                      { icon: 'ri-flashlight-line', label: '24小时实时更新' },
                      { icon: 'ri-group-line', label: '50万+日活用户' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-2 text-sm text-foreground-300">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500/10">
                          <i className={`${item.icon} text-primary-500 text-xs`} />
                        </div>
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20dark%20newsroom%20with%20multiple%20monitors%20displaying%20data%2C%20futuristic%20office%20interior%2C%20red%20accent%20lighting%2C%20professional%20team%20working%2C%20dark%20atmosphere%2C%20cinematic%20composition%2C%20technology%20and%20media%20theme&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="51黑料网品牌形象"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reputation Section */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">
                  平台信誉
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mt-2">
                  值得信赖的信息平台
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {[
                  {
                    icon: 'ri-verified-badge-line',
                    title: '严格审核机制',
                    desc: '所有发布内容均经过专业编辑团队的多重审核，确保信息的真实性和准确性，杜绝虚假信息传播。',
                  },
                  {
                    icon: 'ri-lock-line',
                    title: '用户隐私保护',
                    desc: '严格遵守数据保护法规，用户浏览记录和个人信息均受到最高级别的安全保护，绝不泄露给第三方。',
                  },
                  {
                    icon: 'ri-service-line',
                    title: '专业编辑团队',
                    desc: '拥有一支经验丰富的编辑和调查记者团队，全天候追踪热点事件，为用户提供高质量内容服务。',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/40 text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-500/15">
                      <i className={`${item.icon} text-xl text-accent-500`} />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-100 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">
                  发展历程
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mt-2">
                  我们的成长
                </h2>
              </div>

              <div className="space-y-6">
                {[
                  {
                    year: '2026',
                    title: '平台全面升级',
                    desc: '推出每日大赛专区，新增用户投票互动功能，日均访问量突破50万，成为行业领先的黑料分区导航平台。',
                  },
                  {
                    year: '2025',
                    title: '快速发展',
                    desc: '新增黑料二区板块，扩展内容覆盖面。建立专业编辑团队，引入严格内容审核机制，平台影响力持续扩大。',
                  },
                  {
                    year: '2024',
                    title: '平台创立',
                    desc: '51黑料网正式上线，以黑料一区为核心板块，开启黑料资讯分区导航服务，为用户提供一站式内容聚合体验。',
                  },
                ].map((item, index) => (
                  <div key={item.year} className="flex gap-4 md:gap-6">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-accent-500 font-heading text-sm md:text-base font-bold text-foreground-50 whitespace-nowrap">
                        {item.year}
                      </div>
                      {index < 2 && <div className="w-0.5 flex-1 bg-background-200/50 mt-2" />}
                    </div>
                    <div className="flex-1 pb-6">
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-50 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <span className="text-xs font-medium text-primary-500 uppercase tracking-wider">
                  联系方式
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mt-2">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-400 mt-2">
                  如果您有任何问题、建议或爆料线索，欢迎随时与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[
                  { icon: 'ri-mail-line', title: '电子邮件', value: 'contact@51heiliaowang.com' },
                  { icon: 'ri-wechat-line', title: '微信公众号', value: '51黑料网' },
                  { icon: 'ri-timer-line', title: '工作时间', value: '7×24小时' },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-4 rounded-xl bg-background-100 border border-background-200/40 text-center"
                  >
                    <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-500/10">
                      <i className={`${item.icon} text-primary-500`} />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-200 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Contact Form */}
              <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/40">
                <h3 className="font-heading text-lg font-semibold text-foreground-50 mb-4">
                  发送消息
                </h3>
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-primary-500/15">
                      <i className="ri-check-line text-2xl text-primary-500" />
                    </div>
                    <p className="text-foreground-200 font-medium">消息已发送成功！</p>
                    <p className="text-sm text-foreground-400 mt-1">感谢您的留言，我们会尽快回复。</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-4 px-4 py-2 rounded-lg bg-background-200 text-sm text-foreground-300 hover:text-foreground-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} data-readdy-form="">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="contact_name" className="block text-sm text-foreground-300 mb-1.5">
                          姓名 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact_name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg bg-background-200/50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact_email" className="block text-sm text-foreground-300 mb-1.5">
                          邮箱 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact_email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 rounded-lg bg-background-200/50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact_subject" className="block text-sm text-foreground-300 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact_subject"
                        name="subject"
                        className="w-full px-3 py-2.5 rounded-lg bg-background-200/50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact_message" className="block text-sm text-foreground-300 mb-1.5">
                        消息内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact_message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-lg bg-background-200/50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      />
                    </div>

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website_alt"
                      className="website-alt-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {formError && (
                      <p className="text-sm text-accent-400 mb-4">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={formLoading}
                      className="w-full py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-foreground-50 text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {formLoading ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>

          {/* Legal */}
          <section id="terms" className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-4" id="privacy">
                法律声明
              </h2>
              <div className="space-y-4 text-sm text-foreground-400 leading-relaxed">
                <div>
                  <h3 className="font-semibold text-foreground-200 mb-1">用户协议</h3>
                  <p>
                    使用51黑料网即表示您同意遵守本网站的用户协议。本站内容仅供信息参考，不构成任何形式的建议或承诺。用户应自行判断信息的真实性和可靠性。
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-200 mb-1" id="disclaimer">隐私政策</h3>
                  <p>
                    51黑料网重视用户隐私保护。我们仅收集必要的浏览数据用于改善服务质量，绝不会将您的个人信息出售或分享给第三方。详细的隐私政策请参考相关法律文件。
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-200 mb-1">免责声明</h3>
                  <p>
                    本站所发布的内容均来自公开渠道和用户投稿，51黑料网不对内容的真实性和准确性承担法律责任。如发现侵权或虚假内容，请通过联系方式与我们取得联系，我们将及时处理。本站保留对发布内容的最终解释权。
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}